import React from 'react';
import './V3Addons.scss';

interface Addon {
  id: number;
  name: string;
  price: number;
  discount_price?: number;
  image_url?: string;
  in_stock: boolean;
  has_variants: boolean;
  price_display?: string;
  currency?: {
    symbol: string;
  };
}

interface V3AddonsProps {
  addonsData?: {
    has_addons: boolean;
    processed_addons: Addon[];
  };
  loading?: boolean;
  error?: string | null;
}

const V3Addons: React.FC<V3AddonsProps> = ({
  addonsData,
  loading = false,
  error = null
}) => {

  if (loading) {
    return (
      <div className="v3-addons">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading add-ons...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v3-addons">
        <div className="alert alert-warning" role="alert">
          <strong>Add-ons Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!addonsData?.has_addons || !addonsData.processed_addons?.length) {
    return null;
  }

  const formatPrice = (addon: Addon) => {
    const symbol = addon.currency?.symbol || '$';
    if (addon.discount_price) {
      return `${symbol}${addon.discount_price.toFixed(2)}`;
    }
    return `${symbol}${addon.price.toFixed(2)}`;
  };

  const getAddonCartUrl = (addon: Addon) => {
    return `/addons/${addon.id}/`;
  };

  const handleAddonClick = (e: React.MouseEvent, addon: Addon) => {
    e.preventDefault();
    const url = getAddonCartUrl(addon);
    // Check if this is a React Router route
    if (url.startsWith('/addons/') && url.match(/^\/addons\/\d+\/?$/)) {
      // This matches the React Router route /addons/:addonId
      window.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className="v3-addons">
      <div className="event-content-addons">
        <h3 className="section-title mb-3">
          <i className="fas fa-gift me-2"></i>Add-ons
        </h3>
        <div className="addons-list">
          {addonsData.processed_addons.map((addon) => (
            <a
              key={addon.id}
              href={getAddonCartUrl(addon)}
              onClick={(e) => handleAddonClick(e, addon)}
              className={`addon-item ${!addon.in_stock ? 'disabled' : ''}`}
              data-bs-toggle={addon.has_variants ? 'tooltip' : undefined}
              title={addon.has_variants ? 'Select options' : undefined}
            >
              <div className="addon-image">
                {addon.image_url ? (
                  <img src={addon.image_url} alt={addon.name} loading="lazy" />
                ) : (
                  <div className="no-image">
                    <i className="fas fa-image"></i>
                  </div>
                )}
                {!addon.in_stock && (
                  <div className="sold-out-overlay">
                    <span className="sold-out-badge">Sold Out</span>
                  </div>
                )}
              </div>
              <div className="addon-content">
                <div className="addon-info">
                  <h6 className="addon-title" title={addon.name}>{addon.name}</h6>
                  <div className="addon-price">
                    {addon.discount_price ? (
                      <>
                        <small className="original-price">
                          {addon.currency?.symbol || '$'}{addon.price.toFixed(2)}
                        </small>
                        <span className="current-price">{formatPrice(addon)}</span>
                      </>
                    ) : (
                      <>
                        {addon.has_variants && addon.price_display ? (
                          <>
                            <small className="price-label">From</small>
                            <span className="current-price">{addon.price_display}</span>
                          </>
                        ) : (
                          <span className="current-price">{formatPrice(addon)}</span>
                        )}
                      </>
                    )}
                  </div>
                  {addon.has_variants && (
                    <div className="variants-info">
                      <i className="fas fa-info-circle"></i>
                      <span>Options available</span>
                    </div>
                  )}
                </div>
                <div className="addon-arrow">
                  <i className="fas fa-chevron-right"></i>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default V3Addons;

